<script setup lang="ts">
import { computed } from "vue";
import { useRoute } from "vue-router";

import ReportMetricTooltip from "@/components/datascience/ReportMetricTooltip.vue";
import {
  type CrossValidationReportSummaryDto,
  type EstimatorReportSummaryDto,
  isCrossValidationReport as _isCrossValidationReportCheck,
} from "@/dto";
import { RouteNames } from "@/router";
import { formatNumber } from "@/services/utils";
import { useDatasourceStore } from "@/stores/datasource";

const { report } = defineProps<{
  report: EstimatorReportSummaryDto | CrossValidationReportSummaryDto;
}>();
const route = useRoute();
const dataSource = useDatasourceStore();

const workspacePublicId = computed(() => route.params.workspacePublicId as string);
const projectName = computed(() => route.params.projectName as string);
const isCrossValidationReport = computed(() => _isCrossValidationReportCheck(report));

// Metric values if EstimatorReports, metric means if CVReports
const metrics = computed(
  () =>
    new Map(
      report.metrics
        .filter((m) => m.data_source == dataSource.sourceName)
        .filter((m) => m.position !== null)
        .map((m) => [m.name.replace("_mean", ""), m])
    )
);

const metricsStd = computed(
  () =>
    new Map(
      report.metrics
        .filter((m) => m.data_source == dataSource.sourceName)
        .filter((m) => m.position === null)
        .filter((m) => m.name.endsWith("_std"))
        .map((m) => [m.name.replace("_std", ""), m])
    )
);
</script>

<template>
  <div class="card experiment-search-result-card">
    <div class="header text-base-medium">
      <i class="icon icon-test-tube" />
      <span> {{ report.key }} ({{ report.estimator_class_name }}) </span>
    </div>
    <div class="card-content metrics text-base-medium">
      <div class="metric metric-title text-base-regular">
        <div class="name">
          <i class="icon icon-file" />
          General info
        </div>
        <div v-if="isCrossValidationReport" class="value">(μ ± σ)</div>
      </div>
      <div
        v-for="[name, metric] in metrics"
        :key="name"
        class="metric metric-content text-base-regular"
      >
        <div class="name">
          <span>{{ metric.verbose_name.replace(" - MEAN", "") }}</span>
          <ReportMetricTooltip :metric="metric" />
        </div>
        <div class="value">
          {{ formatNumber(metric.value) }}
          <span v-if="metricsStd.has(name)" class="std">
            ± {{ formatNumber(metricsStd.get(name)!.value) }}
          </span>
        </div>
      </div>
    </div>
    <div class="card-content details text-base-medium">
      <RouterLink
        :to="{
          name: isCrossValidationReport
            ? RouteNames.CrossValidationReportDetail
            : RouteNames.EstimatorReportDetail,
          params: { workspacePublicId, projectName, reportId: report.id },
        }"
      >
        View details <i class="icon icon-arrow-right" />
      </RouterLink>
    </div>
  </div>
</template>

<style scoped>
.experiment-search-result-card {
  display: flex;
  flex-direction: column;
  gap: var(--numbers-4);

  .header {
    display: flex;
    height: 50px;
    align-items: center;
    color: var(--content-secondary);
    gap: var(--numbers-8);

    .icon {
      color: var(--content-secondary);
      font-size: 1.6rem;
    }
  }

  .metrics {
    display: flex;
    flex-direction: column;
    gap: var(--numbers-8);

    .icon {
      color: var(--content-brand);
      vertical-align: text-top;
    }

    .metric {
      display: flex;
      justify-content: space-between;

      .name {
        :deep(.icon) {
          vertical-align: text-top;
        }
      }

      &.metric-title .value {
        color: var(--content-primary);
      }

      &.metric-content .name {
        color: var(--content-tertiary);
      }

      &.metric-content .value {
        color: var(--content-primary);

        .std {
          color: var(--content-branding);
        }
      }
    }
  }

  .details {
    a {
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--content-primary);
      gap: var(--numbers-6);
      text-decoration: none;

      i {
        transition: transform var(--animation-duration) var(--animation-easing);
      }

      &:hover {
        i {
          transform: translateX(2px);
        }
      }
    }
  }
}
</style>
